import AccessType from 'constants/customerAccessType';
import nxModule from 'nxModule';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/concat';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/takeWhile';
import LocalCache from 'shared/utils/localCache';

nxModule.factory('customerCache', function (http, customerService, customerContextService, cacheAccessService, CacheFactory) {
  let that = this;
  let service = {};

  service.loadedCustomerId = null;
  service.loadedCustomerType = null;
  service.hasInvalidData = null;
  service.shouldUpdateProfile = null;
  that.cacheMap = {};

  service.profile = (customerId, includeCoMadeLoans = true, allowInactive = false) =>
    retrieveCacheObject(customerId, 'profile', {
      provider: () => http.get(`/customers/${customerId}${includeCoMadeLoans ? '?includeCoMadeLoans=true' : ''}${allowInactive ? '&allowInactive=true' : ''}`)
        .success(profile => {
          if (profile.customerType === 'GROUP')
            console.error(`ERROR: loaded group customer. customerId: ${customerId}`);
          customerService.extractPhones(profile);
          customerService.formatDates(profile);
          customerService.decodeNewLinesInComments(profile);
          service.loadedCustomerType = profile.customerType;
          service.hasInvalidData = profile.hasInvalidData;
          profile.primaryAddress = profile.addresses.find(a => a.primary);
          customerContextService.loadCustomer(service.loadedCustomerId, service.hasInvalidData);
        })
    });

  /**
   * fullAccess equals false means that stream of data from cache will contain
   * only products that current user has access to.
   */
  service.depositAccounts = (customerId, fullAccess=true) => {
    const cache = retrieveCacheObject(customerId, 'customerDepositAccounts', {
      provider: () => http.get(`/products/accounts/details?customerId=${customerId}&includeOwnership=true`)
    });

    if(fullAccess) {
      return cache;
    }

    return cacheAccessService.withRestrictedAccess(cache, customerId, AccessType.NONGROUP);
  };

  service.relatedDepositAccounts = customerId => {
    return retrieveCacheObject(customerId, 'customerRelatedDepositAccounts', {
      provider: () => http.get(`/products/accounts/related?customerId=${customerId}`)
    });
  };

  service.depositAccountGroups = customerId => {
    return retrieveCacheObject(customerId, 'customerDepositAccountGroups', {
      provider: () => http.get(`/account-groups/customer?customerId=${customerId}`)
    });
  };

  /**
   * fullAccess equals false means that stream of data from cache will contain
   * only products that current user has access to.
   */
  service.loans = (customerId, fullAccess=true) => {
    const cache = retrieveCacheObject(customerId, 'customerLoans', {
      provider: () => http.get(`/products/loans?customerId=${customerId}`)
    });

    if(fullAccess) {
      return cache;
    }

    return cacheAccessService.withRestrictedAccess(cache, customerId, AccessType.NONGROUP);
  };

  service.loanPDCs = (customerId, loanId) => {
    return retrieveCacheObject(customerId, `customerLoanPDCs${ loanId }`, {
      provider: () => http.get(`/products/loans/${ loanId }/post-dated-checks`)
    });
  };

  /**
   * fullAccess equals false means that stream of data from cache will contain
   * only products that current user has access to.
   */
  service.termDeposits = (customerId, fullAccess=true) => {
    const cache = retrieveCacheObject(customerId, 'customerTermDeposits', {
      provider: () => http.get(`/products/deposits?customerId=${customerId}&includeOwnership=true&includePledgeInstallment=true`)
    });

    if(fullAccess) {
      return cache;
    }

    return cacheAccessService.withRestrictedAccess(cache, customerId, AccessType.NONGROUP);
  };

  /**
   * fullAccess equals false means that stream of data from cache will contain
   * only products that current user has access to.
   */
  service.creditLines = (customerId, fullAccess=true) => {
    const cache = retrieveCacheObject(customerId, 'customerCreditLines', {
      provider: () => http.get(`/credit-lines?customerId=${customerId}`)
    });

    if (fullAccess) {
      return cache;
    }

    return cacheAccessService.withRestrictedAccess(cache, customerId, AccessType.NONGROUP);
  } ;

  service.customerProductFees = (customerId) =>
    retrieveCacheObject(customerId, 'customerProductFees', {
      provider: () => http.get(`/products/fees?customerId=${customerId}`)
    });

  // --------- technical functions -------------
  function retrieveCacheObject(customerId, cacheName, cacheOptions) {
    verifyCustomerId(customerId);

    // adds default params
    cacheOptions['cacheStorage'] = 'memory';
    cacheOptions['cacheName'] = `${cacheName}-customer-${customerId}`;
    cacheOptions['cacheFactory'] = CacheFactory;

    if (!that.cacheMap[cacheName]) {
      that.cacheMap[cacheName] = new LocalCache(cacheOptions);
    }
    return that.cacheMap[cacheName];
  }

  service.refetch = () => {
    let keys = Object.keys(that.cacheMap);
    for (let key of keys) {
      console.log('Refetching customer data: ' + key);
      that.cacheMap[key].refetch();
    }
  };

  service.evict = () => {
    service.loadedCustomerId = null;
    service.loadedCustomerType = null;
    service.hasInvalidData = null;
    customerContextService.unloadCustomer();
    resetCacheMap();
  };

  service.evictWithoutCustomerId = () => {
    resetCacheMap();
  };

  service.unloadCustomer = () => {
    service.loadedCustomerId = null;
    service.loadedCustomerType = null;
    service.hasInvalidData = null;
    customerContextService.unloadCustomer();
    service.evict();
  };

  const resetCacheMap = () => {
    const keys = Object.keys(that.cacheMap);
    for (const key of keys) {
      that.cacheMap[key].evict();
    }
    that.cacheMap = {};
  }

  function verifyCustomerId(newCustomerId) {
    if (service.loadedCustomerId && String(service.loadedCustomerId) !== String(newCustomerId)) {
      resetCacheMap();
    }
    service.loadedCustomerId = newCustomerId;
    customerContextService.loadCustomer(service.loadedCustomerId, service.hasInvalidData);
  }

  return service;
});
